import { groupBySection, packColumns } from '../paginate.js';
import { hasTitle, listColumns, listCss, type MenuTemplate } from './common.js';
import {
  NB_FAMILY,
  NB_THEME,
  NB_VARIANTS,
  nbBlock,
  nbBothClass,
  nbColumns,
  nbCss,
  nbFoot,
  nbHeader,
  nbHeaderExtra,
  nbListScale,
} from './night-bites-columns.js';

/**
 * Night Bites, two columns: the same charcoal board, big white title and script tagline, but
 * only two wide columns, each opening with a large line-art icon beside its script heading and
 * set in larger type with faint dotted leaders. For a screen with fewer items, or the second
 * screen of a Night Bites set. Portrait stacks the two halves.
 */

const S = '.mb-t-night-bites-duo';

export const nightBitesDuo: MenuTemplate = {
  id: 'night-bites-duo',
  name: 'Night Bites · Two columns',
  description:
    'Flat charcoal with a big white title and two wide columns under large line-art icons and script headings, in larger type. Matches the other Night Bites screens.',
  defaultTheme: NB_THEME,
  variants: NB_VARIANTS,
  cuisines: ['burgers', 'drinks'],
  family: { ...NB_FAMILY, layout: 'Two columns' },
  uses: { featured: false, sectionImages: true, brand: true, decorations: [] },

  paginate(board, orientation) {
    const portrait = orientation === 'portrait';
    return packColumns(
      groupBySection(board),
      nbColumns(board, listColumns(board, {
        count: 2,
        capacity: (portrait ? 42 - nbHeaderExtra(board) / 2 : 45 - nbHeaderExtra(board)) / nbListScale(board),
        heading: 4.6,
        item: { nameEm: 1.85, descEm: 1.15, perLine: 62, gap: 1.6 },
        // The icon beside a section's first heading is taller than the heading itself.
        extra: (section, continued) => (!continued && hasTitle(section) ? 4.4 : 0),
      }), { sizeRow: 1.7 * 1.25, nameLine: 1.85 * 1.15, nameChars: portrait ? 26 : 20 }),
      { balance: true },
    );
  },

  render(ctx) {
    const cols = ctx.page.columns
      .map((column) => `<div class="nbb-col">${column.map((b) => nbBlock(ctx, b, 'nbb', { leader: true, art: true })).join('')}</div>`)
      .join('');
    return `${nbHeader(ctx, 'nbb', ctx.orientation === 'portrait' ? 10 : 18)}<div class="nbb-cols${nbBothClass(ctx)}">${cols}</div>${nbFoot(ctx, 'nbb')}`;
  },

  css: `
${S}{padding:2.2em 3.5em 3.4em}
${nbCss(S, 'nbb')}
${S} .nbb-title{font-size:7.2em}
${S} .nbb-title.mb-2nd{font-size:2.6em;margin-top:.1em}
${S} .nbb-tag{font-size:1.75em;margin-top:.1em}
${S} .nbb-tag.mb-2nd{font-size:1.4em;margin-top:0}
${S} .nbb-cols{flex:1;min-height:0;display:grid;grid-template-columns:1fr 1fr;margin-top:2.6em}
${S} .nbb-cols.nb-both{font-size:.88em;margin-top:2em}
${S}.mb-portrait{padding:3em 3em 3.6em}
${S}.mb-portrait .nbb-cols{grid-template-columns:1fr;grid-template-rows:1fr 1fr;margin-top:3em}
${S} .nbb-col{min-width:0;min-height:0;display:flex;flex-direction:column;gap:2em;padding:0 3.2em}
${S} .nbb-col:first-child{padding-inline-start:1em}
${S} .nbb-col:last-child{padding-inline-end:1em}
${S} .nbb-col+.nbb-col{border-inline-start:.09em solid var(--nb-rule)}
${S}.mb-portrait .nbb-col{padding:0 1em}
${S}.mb-portrait .nbb-col+.nbb-col{border-inline-start:0;border-top:.09em solid var(--nb-rule);padding-top:2.6em}
${S}.mb-portrait .nbb-col:first-child{padding-bottom:1.6em}
${S} .nbb-head{display:flex;align-items:center;gap:1.4em;margin-bottom:1.6em;padding-bottom:1em;
  border-bottom:.12em solid color-mix(in srgb,var(--mb-accent) 55%,transparent)}
${S} .nbb-art{flex-shrink:0;width:8em;height:8em}
${S} .nbb-htext{min-width:0}
${S} .nbb-st{font-size:3.6em;line-height:1.15}
${S} .nbb-st.mb-2nd{font-size:2.4em}
${S} .nbb-sub{font-size:1.1em;margin-top:.1em}
${S} .nbb-cont .nbb-st{font-size:2.6em}
${listCss(S, 'nbb', { name: '1.85em', price: '1.7em', desc: '1.15em', leader: '.14em dotted color-mix(in srgb,var(--mb-text) 30%,transparent)' })}
${S} .nbb-item{margin-bottom:1.6em}
${S} .nbb-name2{font-size:1.25em}
`,
};
